/**
 * AudienceFitFormSection — reusable form chunk shared by ManualThemeDialog and
 * ManualTopicDialog. Captures the "audience binding" half of the directive
 * (no-AI) creation flow:
 *
 *   - "Applies to all audiences" checkbox + rationale (mutually exclusive
 *     with audienceFit[])
 *   - audienceFit[] rows (audienceBriefRef + fit strength + rationale)
 *
 * Outputs structured AudienceFit[] with source='user_confirmed' on save —
 * the parent dialog wraps the rest of the payload (name, dataUsage…).
 *
 * Validation invariant:
 *   - appliesToAllAudiences=true ⟹ audienceFit[].length === 0
 *   - audienceFit[].length > 0 ⟹ appliesToAllAudiences=false
 *   - At least one of the two must be set (validated by parent on submit).
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Stack,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Button,
  Paper,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { AudienceBriefMultiPicker } from './AudienceBriefMultiPicker';
import type { AudienceBriefRef, AudienceFit } from '@modules/content-plan/types';

const refKey = (r: AudienceBriefRef) => `${r.analysisId}::${r.audienceId}`;

export interface AudienceFitFormSectionValue {
  audienceFit: AudienceFit[];
  appliesToAllAudiences: boolean;
  appliesToAllAudiencesRationale: string;
}

export interface AudienceFitFormSectionProps {
  projectId: string;
  value: AudienceFitFormSectionValue;
  onChange: (next: AudienceFitFormSectionValue) => void;
}

const EMPTY_VALUE: AudienceFitFormSectionValue = {
  audienceFit: [],
  appliesToAllAudiences: false,
  appliesToAllAudiencesRationale: '',
};

export const AUDIENCE_FIT_FORM_EMPTY_VALUE = EMPTY_VALUE;

export const AudienceFitFormSection: React.FC<AudienceFitFormSectionProps> = ({
  projectId,
  value,
  onChange,
}) => {
  // Local picker state for which refs are "selected" — we derive audienceFit
  // rows from this list. Each row exposes fit + rationale.
  const selectedRefs = useMemo(
    () => value.audienceFit
      .map((f) => f.audienceBriefRef)
      .filter(Boolean) as AudienceBriefRef[],
    [value.audienceFit],
  );

  // Sync rationale field when toggling universal mode off
  useEffect(() => {
    if (value.appliesToAllAudiences && value.audienceFit.length > 0) {
      // Mutually exclusive: silent guard — clear audienceFit[] when universal flipped on
      onChange({ ...value, audienceFit: [] });
    }
  }, [value.appliesToAllAudiences]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleUniversal = (checked: boolean) => {
    if (checked) {
      onChange({
        ...value,
        appliesToAllAudiences: true,
        audienceFit: [],
      });
    } else {
      onChange({
        ...value,
        appliesToAllAudiences: false,
        appliesToAllAudiencesRationale: '',
      });
    }
  };

  const handleRefsChange = (next: AudienceBriefRef[]) => {
    // Preserve existing fit/rationale by ref key
    const existingByKey = new Map(
      value.audienceFit
        .filter((f) => f.audienceBriefRef)
        .map((f) => [refKey(f.audienceBriefRef!), f]),
    );
    const merged: AudienceFit[] = next.map((ref) => {
      const existing = existingByKey.get(refKey(ref));
      if (existing) return existing;
      return {
        audienceBriefRef: ref,
        fit: 'medium' as any,
        rationale: '',
        source: 'user_confirmed' as any,
      } as AudienceFit;
    });
    onChange({ ...value, audienceFit: merged });
  };

  const updateFitAt = (index: number, patch: Partial<AudienceFit>) => {
    onChange({
      ...value,
      audienceFit: value.audienceFit.map((f, i) => (i === index ? { ...f, ...patch } : f)),
    });
  };

  const removeFitAt = (index: number) => {
    onChange({
      ...value,
      audienceFit: value.audienceFit.filter((_, i) => i !== index),
    });
  };

  return (
    <Stack spacing={1.5} data-testid="audience-fit-form-section">
      <FormControlLabel
        control={
          <Checkbox
            checked={value.appliesToAllAudiences}
            onChange={(e) => handleToggleUniversal(e.target.checked)}
            data-testid="audience-fit-applies-to-all"
          />
        }
        label="Applies to all selected audiences (universal)"
      />
      {value.appliesToAllAudiences ? (
        <TextField
          label="Why universal?"
          value={value.appliesToAllAudiencesRationale}
          onChange={(e) =>
            onChange({ ...value, appliesToAllAudiencesRationale: e.target.value })
          }
          fullWidth
          multiline
          minRows={2}
          helperText="Required when 'universal'. Explain why this content's value is cross-cutting (e.g. brand voice, category education)."
          inputProps={{ 'data-testid': 'audience-fit-universal-rationale' }}
        />
      ) : (
        <>
          <Typography variant="caption" color="text.secondary">
            Pick audiences below, then for each provide fit strength + a 1-2 sentence rationale.
          </Typography>
          <AudienceBriefMultiPicker
            projectId={projectId}
            value={selectedRefs}
            onChange={handleRefsChange}
            maxListHeight={260}
          />
          {value.audienceFit.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Per-audience fits ({value.audienceFit.length})
              </Typography>
              <Stack spacing={1}>
                {value.audienceFit.map((f, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                        {f.audienceBriefRef?.labelSnapshot ?? f.audienceBriefRef?.audienceId}
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id={`fit-strength-${i}`}>Fit strength</InputLabel>
                        <Select
                          labelId={`fit-strength-${i}`}
                          label="Fit strength"
                          value={(f.fit as unknown as string) ?? 'medium'}
                          onChange={(e) => updateFitAt(i, { fit: e.target.value as any })}
                          inputProps={{ 'data-testid': `audience-fit-strength-${i}` }}
                        >
                          <MenuItem value="strong">strong</MenuItem>
                          <MenuItem value="medium">medium</MenuItem>
                          <MenuItem value="weak">weak</MenuItem>
                        </Select>
                      </FormControl>
                      <IconButton
                        size="small"
                        onClick={() => removeFitAt(i)}
                        aria-label="Remove audience fit"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <TextField
                      label="Rationale"
                      value={f.rationale ?? ''}
                      onChange={(e) => updateFitAt(i, { rationale: e.target.value })}
                      fullWidth
                      multiline
                      minRows={2}
                      helperText="Reference mainBarriers / proofNeeds / editorialAngles from the brief."
                      inputProps={{ 'data-testid': `audience-fit-rationale-${i}` }}
                    />
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};

export default AudienceFitFormSection;
